import React, { useMemo } from 'react';
import classnames from 'classnames';
import GameDefault from '@/pages/home/assets/img/common/game-default.svg';
import DragHandle from '@sg/assests/svg/draghandle-icon.svg';
import LiveMetricBoard from '../LiveMetricBoard';
// import EventsCell from '../EventsCell'; // 旧版事件卡片
import EventsTimelineCell from '../EventsTimelineCell';
import {
  formatMoM,
  formatShortMoney,
  formatPercent,
  buildTrendData,
  getMetricNumericValue,
} from './utils';
import {
  getCurrencySymbol,
  resolveLiveTitleCurrencyCode,
} from '@sg/pages/StudioManagement/LiveTitles/utils/currencyRules';
import './index.scss';

interface IProjectRowProps {
  project: any;
  isEditing?: boolean;
  dragHandleProps?: any;
  className?: string;
  onIconClick?: (project: any) => void;
  activeOverview?: string;
  selectedMonth?: any;
}

const CLS = 'dbs-lto-project-row';

const MetricCellNew: React.FC<{
  metricName: string;
  title: string;
  metric: any;
  numValue: number | null;
  formatter?: string;
  unit?: string;
  extraLines?: React.ReactNode;
  withDivider?: boolean;
}> = ({ metricName, title, metric, numValue, formatter, unit, extraLines, withDivider = false }) => {
  const textList = useMemo(
    () => [{ value: 'MoM', text: formatMoM(metric?.mom) }],
    [metric?.mom],
  );

  const trendData = useMemo(
    () => buildTrendData(metric?.timeline, metricName),
    [metricName, metric?.timeline],
  );

  return (
    <div
      className={classnames(
        `${CLS}__metric`,
        `${CLS}__metric--board`,
        withDivider && `${CLS}__metric--with-divider`,
      )}
    >
      <LiveMetricBoard
        title={title}
        num={numValue}
        formatter={formatter}
        unit={unit}
        textList={textList}
        trendData={trendData}
        extraLines={extraLines}
      />
    </div>
  );
};

const ProjectRow: React.FC<IProjectRowProps> = (props) => {
  const {
    project,
    isEditing = false,
    dragHandleProps,
    className,
    onIconClick,
    selectedMonth,
  } = props;

  const grossRevenueCurrencyCode = useMemo(
    () =>
      resolveLiveTitleCurrencyCode({
        // LiveVisualizationAll project rows currently have no unified_id in row data,
        // so use game_title for currency resolution fallback.
        gameTitle: project?.game_title,
      }),
    [project?.game_title],
  );
  const grossRevenueCurrencySymbol = useMemo(
    () => getCurrencySymbol(grossRevenueCurrencyCode),
    [grossRevenueCurrencyCode],
  );

  // EventsCell 仍按旧 { name } 字段渲染，这里把新结构 { event_content } 适配过去
  // const eventsForCell = useMemo(
  //   () =>
  //     (project?.events || []).map((ev: any) => ({
  //       name: ev?.event_content,
  //       priority: ev?.priority || 'Medium',
  //     })),
  //   [project?.events],
  // );

  const content = (
    <div className={classnames(CLS, className)}>
      <div
        className={classnames(`${CLS}__identity`, `${CLS}__identity--with-divider`)}
        onClick={() => onIconClick?.(project)}
      >
        <img className={`${CLS}__icon`} src={project?.cover || GameDefault} alt={project?.game_title} />
        <div className={`${CLS}__name`}>{project?.game_title}</div>
      </div>

      <MetricCellNew
        metricName="gross_revenue"
        title={`Gross Revenue (${grossRevenueCurrencySymbol})`}
        metric={project?.gross_revenue}
        numValue={getMetricNumericValue('gross_revenue', project)}
        formatter="db_bmk0"
        // unit="USD"
        withDivider
        extraLines={
          <div className={`${CLS}__ytm`}>
            <div className={`${CLS}__ytm-row`}>
              <span className={`${CLS}__ytm-label`}>YTM Actual</span>
              <strong className={`${CLS}__ytm-value`}>
                {formatShortMoney(project?.gross_revenue?.ytm_actual)}
              </strong>
            </div>
            <div className={`${CLS}__ytm-row`}>
              <span className={`${CLS}__ytm-label`}>YTM KPI</span>
              <strong className={`${CLS}__ytm-value`}>
                {formatShortMoney(project?.gross_revenue?.ytm_kpi)}
              </strong>
            </div>
            <div className={`${CLS}__ytm-completion`}>
              (KPI Completion: {formatPercent(project?.gross_revenue?.kpi_completion)})
            </div>
          </div>
        }
      />
      <MetricCellNew
        metricName="mau"
        title="MAU"
        metric={project?.mau}
        numValue={getMetricNumericValue('mau', project)}
        formatter="db_bmk0"
        withDivider
      />
      <MetricCellNew
        metricName="avg_dau"
        title="AVG.DAU"
        metric={project?.avg_dau}
        numValue={getMetricNumericValue('avg_dau', project)}
        formatter="db_bmk0"
        withDivider
      />
      {/* 旧版事件卡片  */}
      {/* <EventsCell
        events={eventsForCell}
        cover={project?.cover || GameDefault}
        gameTitle={project?.game_title}
        studioName={activeOverview === 'mc' ? 'Miniclip' : 'Live-Ops'}
      /> */}
      <EventsTimelineCell
        events={project?.events}
        selectedMonth={selectedMonth}
      />
    </div>
  );

  return (
    <div className={`${CLS}-drag-handle-wrapper`}>
      {isEditing && (
        <div className={`${CLS}-drag-handle`} {...dragHandleProps}>
          <img className={`${CLS}-drag-handle__icon`} src={DragHandle} alt="Drag handle" />
        </div>
      )}
      {content}
    </div>
  );
};

export default ProjectRow;
